/* eslint-disable no-underscore-dangle */
import { Alert, Button } from '@fountain/fountain-ui-components';
import { Grid, Typography } from '@material-ui/core';
import {
  WorkflowDocumentSignatureStage,
  WorkflowDocumentSigningStage,
  WorkflowStageDetail,
} from 'api-clients/monolith';
import { produce } from 'immer';
import React, { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { v4 as uuid } from 'uuid';

import { DEFAULT_DOCUMENT_SIGNING_RULE } from 'containers/WorkflowEditor/components/Rules/constants';
import { collectGroupErrorMessages } from 'containers/WorkflowEditor/components/WorkflowEditorContent/StageDetail/DocumentSigningStage/Documents/errorUtils';

import { DocumentSigningRuleCard } from './DocumentSigningRuleCard';
import { messages } from './messages';
import { useStyles } from './styles';
import { DocumentSigningRuleProps, OtherSetSignerMappings } from './types';

/**
 * The offer letter attachments every rule set OTHER than `exceptRuleId` holds,
 * grouped by template (AXHE-4608). Feeds the modal's refusal to countersign one
 * template two ways across rules, which the send path cannot resolve.
 */
const otherSetSignerMappings = (
  rules: DocumentSigningRuleProps,
  exceptRuleId: string,
): OtherSetSignerMappings => {
  const mappings: OtherSetSignerMappings = {};

  Object.entries(rules).forEach(([id, rule]) => {
    if (id === exceptRuleId) return;

    (
      rule.template_set_attributes?.offer_letter_templates_stages_attributes ??
      []
    ).forEach(assignment => {
      // eslint-disable-next-line no-underscore-dangle
      if (assignment._destroy) return;
      const templateId = assignment.offer_letter_template_id;
      if (templateId == null) return;

      mappings[templateId] = [
        ...(mappings[templateId] ?? []),
        assignment.signers_mapping ?? {},
      ];
    });
  });

  return mappings;
};

export interface DocumentSigningRulesProps {
  stage: WorkflowDocumentSigningStage | WorkflowDocumentSignatureStage;
  setStage: React.Dispatch<React.SetStateAction<WorkflowStageDetail>>;
  setDocumentSigningRules: React.Dispatch<
    React.SetStateAction<DocumentSigningRuleProps>
  >;
  documentSigningRules: DocumentSigningRuleProps;
  errors?: Record<string, Array<string>>;
}

export const DocumentSigningRules: FC<DocumentSigningRulesProps> = ({
  stage,
  documentSigningRules,
  setDocumentSigningRules,
  errors,
  setStage,
}) => {
  const styles = useStyles();
  const intl = useIntl();

  const availableRules = { ...documentSigningRules };

  // Filter out any documentSigningRules that have _destroy set to true
  Object.keys(availableRules).forEach(ruleId => {
    if (availableRules[ruleId]._destroy) {
      delete availableRules[ruleId];
    }
  });

  // Collect all group_error messages for template_rule_set_attributes
  const groupErrorMessages = collectGroupErrorMessages(errors, key => {
    return (
      key.startsWith('template_rule_set_attributes') &&
      key.endsWith('.group_error')
    );
  });

  const onAddRule = () => {
    setDocumentSigningRules(
      produce((draftRules: DocumentSigningRuleProps) => {
        draftRules[uuid()] = DEFAULT_DOCUMENT_SIGNING_RULE;
      }),
    );
  };

  const onDeleteRule = (ruleId: string | number) => {
    setDocumentSigningRules(
      produce((draftRules: DocumentSigningRuleProps) => {
        draftRules[ruleId]._destroy = true;
      }),
    );

    // This is used to set _destroy on any embedded_signature_template_stage (signers) that are no longer being used in the stage
    setStage(
      produce(stage, draftStage => {
        const allRulesDeleted = Object.keys(availableRules).length === 1;

        // destroy the template_rule_set if the last document is destroyed
        if (
          allRulesDeleted &&
          draftStage.additional_info.template_rule_set_attributes
        ) {
          draftStage.additional_info.template_rule_set_attributes._destroy = true;
        }
        // Looping through a given rule.template_settings_attributes and checking that the same template / document doesn't
        // exist in "default documents" and "alternate documents"
        availableRules?.[
          ruleId
        ]?.template_set_attributes?.template_settings_attributes?.forEach(
          templateSetting => {
            const id = templateSetting.template_id;

            const isDefaultTemplateAlreadyUsed =
              stage.additional_info.else_template_set_attributes?.template_settings_attributes?.some(
                setting => !setting._destroy && setting.template_id === id,
              );

            const copyDocumentSigningRules = { ...documentSigningRules };

            // Filtering out the rule itself from consideration
            delete copyDocumentSigningRules[ruleId];

            const isAlternateTemplateAlreadyUsed = Object.values(
              copyDocumentSigningRules,
            ).some(rule => {
              const templateSettings =
                rule.template_set_attributes.template_settings_attributes;

              return templateSettings?.some(
                setting => !setting._destroy && setting.template_id === id,
              );
            });

            // Removing embedded_signature_template_stage (signers) if this is the only instance of the template / document
            if (
              !isDefaultTemplateAlreadyUsed &&
              !isAlternateTemplateAlreadyUsed &&
              draftStage.type === 'DocumentSigningStage' &&
              draftStage.additional_info
                .embedded_signature_templates_stages_attributes
            ) {
              const draftEmbeddedSignatureTemplatesStages =
                draftStage.additional_info
                  .embedded_signature_templates_stages_attributes;

              const idx = draftEmbeddedSignatureTemplatesStages.findIndex(
                ests => ests.embedded_signature_template_id === id,
              );

              if (draftEmbeddedSignatureTemplatesStages[idx]) {
                draftEmbeddedSignatureTemplatesStages[idx]._destroy = true;
              }
            }
          },
        );
      }),
    );
  };

  if (Object.keys(availableRules).length === 0) {
    return (
      <>
        {groupErrorMessages.length > 0 && (
          <Alert fullWidth type="danger" className={styles.errorsAlert}>
            {/* TODO: this needs to be translated! */}
            <span>{groupErrorMessages.join(', ')}</span>
          </Alert>
        )}
        <Button
          type="secondary"
          size="small"
          autoWidth
          onClick={onAddRule}
          disableRipple
          aria-label={intl.formatMessage(messages.addDocumentSet)}
          className={styles.noDocumentSetsAddButton}
        >
          <Typography variant="h4" color="primary">
            <FormattedMessage {...messages.addDocumentSet} />
          </Typography>
        </Button>
      </>
    );
  }

  return (
    <Grid className={styles.rulesContainer}>
      {groupErrorMessages.length > 0 && (
        <Alert fullWidth type="danger" className={styles.errorsAlert}>
          {/* TODO: this needs to be translated! */}
          <span>{groupErrorMessages.join(', ')}</span>
        </Alert>
      )}
      {Object.keys(availableRules as Record<string, unknown>).map(
        (ruleId, index) => {
          const rule = availableRules[ruleId];
          // Filter errors to only include those for this specific rule index
          const ruleSpecificErrors: Record<string, Array<string>> | undefined =
            errors
              ? Object.keys(errors).reduce<Record<string, Array<string>>>(
                  (acc, key) => {
                    // Check if this error is for the current rule index
                    const ruleIndexMatch = key.match(
                      /rules_attributes\[(\d+)\]/,
                    );
                    const errorRuleIndex = ruleIndexMatch
                      ? parseInt(ruleIndexMatch[1], 10)
                      : null;

                    // Only include errors that match this specific rule index
                    // Exclude else_template_set_attributes errors (those belong to the main Documents component)
                    if (
                      errorRuleIndex === index &&
                      key.startsWith('template_rule_set_attributes') &&
                      key.endsWith('.group_error')
                    ) {
                      const errorValue = errors[key];
                      if (Array.isArray(errorValue)) {
                        // eslint-disable-next-line no-param-reassign
                        acc[key] = errorValue;
                      }
                    }
                    return acc;
                  },
                  {},
                )
              : undefined;

          return (
            <DocumentSigningRuleCard
              key={ruleId}
              stage={stage}
              ruleId={ruleId}
              rule={rule}
              index={index}
              setDocumentSigningRules={setDocumentSigningRules}
              setStage={setStage}
              onDeleteRule={onDeleteRule}
              errors={ruleSpecificErrors}
              availableEmbeddedSignatureTemplates={
                // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
                stage.additional_info.available_embedded_signature_templates ||
                []
              }
              otherSetSignerMappings={otherSetSignerMappings(
                availableRules,
                ruleId,
              )}
            />
          );
        },
      )}
      <Button
        type="secondary"
        size="small"
        autoWidth
        onClick={onAddRule}
        disableRipple
        aria-label={intl.formatMessage(messages.addDocumentSet)}
        className={styles.addDocumentSetButton}
      >
        <Typography variant="h4" color="primary">
          <FormattedMessage {...messages.addDocumentSet} />
        </Typography>
      </Button>
    </Grid>
  );
};
